import React, { useCallback, useState } from 'react';
import { Button, Flex, Heading, Text, TextField } from '@vibe/core';
import { useApprovalStore } from '../../stores/approvalStore';
import {
  submitTimesheet,
  resubmitTimesheet,
} from '../../api/timesheet-approval.api';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';
import type { TimesheetDateRange } from '../../types/timesheet.types';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

export interface TimesheetSubmitDialogProps {
  currentUserId: string;
  dateRange: TimesheetDateRange;
  onSuccess: (submission: TimesheetSubmission) => void;
  onClose: () => void;
}

/**
 * Confirmation dialog for submitting or resubmitting a timesheet.
 * Shows the period, a summary, and an optional note field.
 */
const TimesheetSubmitDialog: React.FC<TimesheetSubmitDialogProps> = ({
  currentUserId,
  dateRange,
  onSuccess,
  onClose,
}) => {
  const mySubmission = useApprovalStore((s) => s.mySubmission);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isResubmit =
    mySubmission?.status === 'rejected' || mySubmission?.status === 'reopened';

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    try {
      let result: TimesheetSubmission;

      if (isResubmit && mySubmission) {
        result = await resubmitTimesheet(mySubmission.id, {
          note: note || undefined,
        });
      } else {
        result = await submitTimesheet({
          periodStart: dateRange.start,
          periodEnd: dateRange.end,
          note: note || undefined,
        });
      }

      onSuccess(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [isResubmit, mySubmission, note, dateRange, onSuccess]);

  return (
    <div className="rp-ts-dialog-overlay" onClick={onClose}>
      <div
        className="rp-ts-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <Heading type={Heading.types.H3}>
          {isResubmit ? 'Resubmit Timesheet' : 'Submit Timesheet'}
        </Heading>

        <div className="rp-ts-dialog__body">
          <Text type={Text.types.TEXT2}>
            Period: {dateRange.start} to {dateRange.end}
          </Text>

          {mySubmission && (
            <div className="rp-ts-dialog__hours-summary">
              <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                Total: {formatHoursMinutes(Number(mySubmission.totalHours))}
                {mySubmission.billableHours != null && (
                  <span style={{ color: '#0073ea', fontWeight: 500 }}>
                    {' '}({formatHoursMinutes(Number(mySubmission.billableHours))} billable)
                  </span>
                )}
              </Text>
            </div>
          )}

          {mySubmission && isResubmit && mySubmission.rejectionReason && (
            <div className="rp-ts-dialog__rejection">
              <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                Previous rejection: {mySubmission.rejectionReason}
              </Text>
            </div>
          )}

          <TextField
            title="Note (optional)"
            placeholder="Add a note for the approver..."
            value={note}
            onChange={(val: string) => setNote(val)}
            size={TextField.sizes.MEDIUM}
          />

          {error && (
            <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
              {error}
            </Text>
          )}
        </div>

        <Flex
          gap={Flex.gaps.SMALL}
          justify={Flex.justify.END}
          className="rp-ts-dialog__actions"
        >
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            onClick={handleSubmit}
            loading={submitting}
          >
            {isResubmit ? 'Resubmit' : 'Submit'}
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default TimesheetSubmitDialog;
